import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { SupportedLocale } from '@/lib/constants'
import { setLocale } from '@/lib/i18n'
import { can, isStaff, type Capability } from '@/lib/permissions'
import { api, getAccessToken, setAccessToken } from '@/services/api'
import type { AppRole, ProfileRow } from '@/types'

export type AuthUser = {
  id: string
  email: string
}

export type AuthSession = {
  access_token: string
  user: AuthUser
}

export type ReybexAccount = {
  id?: string
  tenantId?: string
  username?: string
  firstname?: string
  surname?: string
  email?: string
  phone?: string
  mobile?: string
  language?: string
  timeZone?: string
  position?: string
  enabled?: boolean
  accountLocked?: boolean
  accountExpired?: boolean
  isPartnerAccount?: boolean
  synced_at: string
}

type SessionResponse = {
  access_token: string
  user: AuthUser
  profile: ProfileRow
  role: AppRole
  auth_provider?: 'local' | 'reybex'
  reybex_account?: ReybexAccount | null
}

export const useAuthStore = defineStore('auth', () => {
  const session = ref<AuthSession | null>(null)
  const user = ref<AuthUser | null>(null)
  const profile = ref<ProfileRow | null>(null)
  const role = ref<AppRole | null>(null)
  const authProvider = ref<'local' | 'reybex'>('local')
  const reybexAccount = ref<ReybexAccount | null>(null)
  const initialized = ref(false)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!session.value && !!user.value)
  const isActive = computed(() => profile.value?.is_active !== false)
  const userId = computed(() => user.value?.id ?? null)
  const displayName = computed(
    () => profile.value?.full_name || user.value?.email?.split('@')[0] || '',
  )
  const staff = computed(() => isStaff(role.value))

  function hasCapability(capability: Capability): boolean {
    return can(role.value, capability)
  }

  function applyPayload(payload: SessionResponse): void {
    setAccessToken(payload.access_token)
    session.value = { access_token: payload.access_token, user: payload.user }
    user.value = payload.user
    profile.value = payload.profile
    role.value = payload.role
    authProvider.value = payload.auth_provider === 'reybex' ? 'reybex' : 'local'
    reybexAccount.value = authProvider.value === 'reybex' ? (payload.reybex_account ?? null) : null
  }

  function clearSession(): void {
    setAccessToken(null)
    session.value = null
    user.value = null
    profile.value = null
    role.value = null
    authProvider.value = 'local'
    reybexAccount.value = null
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return

    try {
      const token = getAccessToken()
      if (token) {
        const payload = await api<SessionResponse>('/auth/me')
        applyPayload(payload)
      } else {
        clearSession()
      }
    } catch {
      clearSession()
    } finally {
      initialized.value = true
    }
  }

  async function signIn(
    identity: string,
    password: string,
    provider: 'local' | 'reybex' = 'local',
  ): Promise<void> {
    loading.value = true
    try {
      const payload = await api<SessionResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(
          provider === 'reybex'
            ? { provider, username: identity, password }
            : { provider: 'local', email: identity, password },
        ),
      })
      applyPayload(payload)
      if (profile.value && !profile.value.is_active) {
        clearSession()
        throw new Error('account is deactivated')
      }
    } finally {
      loading.value = false
    }
  }

  async function signUp(email: string, password: string, fullName: string): Promise<void> {
    loading.value = true
    try {
      await api('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, full_name: fullName }),
      })
    } finally {
      loading.value = false
    }
  }

  async function signOut(): Promise<void> {
    clearSession()
  }

  async function requestPasswordReset(email: string): Promise<void> {
    await api('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async function updatePassword(password: string, token?: string | null): Promise<void> {
    if (token) {
      const payload = await api<SessionResponse>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      })
      applyPayload(payload)
      return
    }
    await api('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  }

  async function updateProfile(changes: Partial<ProfileRow>): Promise<void> {
    if (!user.value) return
    const data = await api<ProfileRow>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({
        full_name: changes.full_name,
        avatar_url: changes.avatar_url,
        locale: changes.locale,
        timezone: changes.timezone,
        ...(changes.onboarding_role !== undefined
          ? { onboarding_role: changes.onboarding_role }
          : {}),
        ...(changes.onboarding_level !== undefined
          ? { onboarding_level: changes.onboarding_level }
          : {}),
        ...(changes.onboarding_focus !== undefined
          ? { onboarding_focus: changes.onboarding_focus }
          : {}),
      }),
    })
    profile.value = data
    if (data.locale) setLocale(data.locale as SupportedLocale)
  }

  async function refreshSession(): Promise<void> {
    const payload = await api<SessionResponse>('/auth/me')
    applyPayload(payload)
  }

  async function loadProfile(): Promise<void> {
    if (!user.value) {
      profile.value = null
      return
    }
    await refreshSession()
  }

  return {
    session,
    user,
    profile,
    role,
    authProvider,
    reybexAccount,
    initialized,
    loading,
    isAuthenticated,
    isActive,
    userId,
    displayName,
    staff,
    hasCapability,
    can: hasCapability,
    initialize,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    updatePassword,
    updateProfile,
    refreshSession,
    loadProfile,
  }
})
